// AdminUserCourseList.jsx
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faPlus,
    faEdit,
    faFileImport,
    faFileExport,
    faChevronRight,
    faChevronLeft,
    faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
    Card,
    Table,
    InputGroup,
    Form,
    Button,
    Modal,
    Badge,
} from "@themesberg/react-bootstrap";
import {
    adminGetCoursesByUserId,
    adminAddCourse,
    adminEditCourse,
    adminImportCourseList,
    adminExportCourseList,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
    background: "#1D4E89",
    border: "none",
    borderRadius: "0.5rem",
    color: "#ffffff",
};

const initialCourses = [
    { id: 1, title: "React Basics", price: "499" },
    { id: 2, title: "Laravel Fundamentals", price: "999" },
    { id: 3, title: "Bootstrap Mastery", price: "799" },
];

const mapCourseItem = (item) => ({
    id: item?.id ?? Date.now(),
    title: item?.title || "",
    price: item?.price || "",
    description: item?.description || "",
    created_at: item?.created_at || "",
    status: item?.status || "",
});

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "success":
      return { background: "#10b981", color: "#ffffff" };
    case "completed":
      return { background: "#10b981", color: "#ffffff" };
    case "pending":
      return { background: "#f59e0b", color: "#ffffff" };
    case "failed":
      return { background: "#ef4444", color: "#ffffff" };
    case "refunded":
      return { background: "#6366f1", color: "#ffffff" };
    default:
      return { background: "#6b7280", color: "#ffffff" };
  }
};

export default function AdminUserCourseList() {
    const { id: userId } = useParams();
    const navigate = useNavigate();

    const [search, setSearch] = useState("");
    const [courses, setCourses] = useState(initialCourses);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const importFileRef = useRef(null);
    const searchDebounceRef = useRef(null);
    const initialLoadRef = useRef(true);

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const clearSearchDebounce = () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
    };

    const fetchCourses = async (page = 1, perPage = 10, searchTerm = "") => {
        if (!userId) {
            setCourses([]);
            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            });
            return;
        }

        try {
            setLoading(true);

            const params = {
                page,
                per_page: perPage,
            };

            const trimmedSearch = String(searchTerm || "").trim();
            if (trimmedSearch) {
                params.search = trimmedSearch;
            }

            const response = await adminGetCoursesByUserId(userId, params);
            const responseData = response?.data || {};
            const apiData = responseData?.data || {};
            const apiCourses = Array.isArray(apiData?.courses) ? apiData.courses : [];
            const apiPagination = apiData?.pagination || {};

            setCourses(apiCourses.length > 0 ? apiCourses.map(mapCourseItem) : []);
            setPagination({
                current_page: apiPagination.current_page ?? 1,
                last_page: apiPagination.last_page ?? 1,
                per_page: apiPagination.per_page ?? 10,
                total: apiPagination.total ?? apiCourses.length,
            });
        } catch (error) {
            console.error("Failed to fetch courses:", error);
            toast.error(error?.response?.data?.message || "Unable to fetch course list.");
            setCourses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            fetchCourses(1, pagination.per_page, "");
            return;
        }

        clearSearchDebounce();
        searchDebounceRef.current = setTimeout(() => {
            fetchCourses(1, pagination.per_page, search);
        }, 300);

        return () => {
            clearSearchDebounce();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, userId]);

    const filteredData = courses;

    const getShowingRange = () => {
        const total = pagination.total || 0;
        const current = pagination.current_page || 1;
        const perPage = pagination.per_page || 10;

        if (total === 0) {
            return { start: 0, end: 0 };
        }

        const start = (current - 1) * perPage + 1;
        const end = Math.min(current * perPage, total);

        return { start, end };
    };

    const getPageItems = () => {
        const totalPages = pagination.last_page || 1;
        const current = pagination.current_page || 1;

        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (current <= 2) {
            return [1, 2, 3, "...", totalPages];
        }

        if (current >= totalPages - 1) {
            return [1, "...", totalPages - 2, totalPages - 1, totalPages];
        }

        return [1, "...", current - 1, current, current + 1, "...", totalPages];
    };

    const { start, end } = getShowingRange();

    return (
        <>
            <div className="course-header py-4">
                <div className="course-header-title">
                    <h2 className="h4 mb-1">User Course List</h2>
                </div>

                <div className="course-header-actions">
                    <InputGroup className="course-search">
                        <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faSearch} className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ boxShadow: "none" }}
                        />
                    </InputGroup>

                    <Button type="button" variant="outline-primary" onClick={() => navigate(-1)} className="course-back-btn">
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm rounded-4">
                <Card.Body className="p-4">
                    {/* <div className="course-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div>
                            <h5 className="mb-1">Course List</h5>
                            <div className="text-muted small">Latest course entries appear on top</div>
                        </div>


                    </div> */}

                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Sr No</th>
                                    <th>Course Title</th>
                                    <th>Price</th>
                                    <th className="text-center">Purchase Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-5">
                                            Loading courses...
                                        </td>
                                    </tr>
                                ) : filteredData.length > 0 ? (
                                    filteredData.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="fw-semibold">{start + index}</td>
                                            <td>{item.title}</td>
                                            <td>₹{Number(item.price || 0).toLocaleString("en-IN")}</td>
                                            <td className="text-center">
                                                                      <Badge
                                                                        className="rounded-pill d-inline-flex align-items-center justify-content-center"
                                                                        style={{
                                                                          ...getStatusBadgeStyle(item.status),
                                                                          minWidth: "5.5rem",
                                                                          height: "2rem",
                                                                          padding: "0 12px",
                                                                        }}
                                                                      >
                                                                        {capitalize(item.status || "N/A")}
                                                                      </Badge>
                                                                    </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-5">
                                            No courses found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted small">
                            Showing {start} to {end} of {pagination.total} entries
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={pagination.current_page <= 1 || loading}
                                onClick={() => {
                                    clearSearchDebounce();
                                    fetchCourses(
                                        Math.max(1, pagination.current_page - 1),
                                        pagination.per_page,
                                        search
                                    );
                                }}
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </Button>

                            {getPageItems().map((item, index) =>
                                item === "..." ? (
                                    <span key={`dots-${index}`} className="text-muted px-1">
                                        ...
                                    </span>
                                ) : (
                                    <Button
                                        key={item}
                                        type="button"
                                        variant="outline-primary"
                                        disabled={loading}
                                        onClick={() => {
                                            clearSearchDebounce();
                                            fetchCourses(item, pagination.per_page, search);
                                        }}
                                        className={`course-page-btn ${item === pagination.current_page
                                            ? "course-page-btn-active"
                                            : ""
                                            }`}
                                    >
                                        {item}
                                    </Button>
                                )
                            )}

                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={
                                    pagination.current_page >= pagination.last_page || loading
                                }
                                onClick={() => {
                                    clearSearchDebounce();
                                    fetchCourses(
                                        Math.min(pagination.last_page, pagination.current_page + 1),
                                        pagination.per_page,
                                        search
                                    );
                                }}
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <style>{`
       .course-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 0;
}

.course-header-title {
  flex: 0 0 auto;
}

.course-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex: 1;
}

.course-search {
  width: 100%;
  max-width: 300px;
  flex: 0 1 360px;
  min-width: 0;
}

.course-back-btn {
  flex: 0 0 auto;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .course-header {
    flex-direction: column;
    align-items: stretch;
  }

  .course-header-actions {
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: nowrap;
  }

  .course-search {
    flex: 1 1 auto;
    max-width: none;
    min-width: 0;
  }

  .course-back-btn {
    flex: 0 0 auto;
    white-space: nowrap;
    padding: 6px 10px;
  }
}       
      `}</style>
        </>
    );
}